import { useStyles } from './styles'
import { CaseScaledCard } from '@/shared/ui/caseScaledCard'
import { lang } from '@/shared/constants/lang'
import { useUnit } from 'effector-react'
import { $userBalance, $userLanguage, $userToken, authUserFx } from '@/entities/user'
import { goToAuth, goToShop } from '@/app/routing/events'
import {
  $selectedPurchaseOption,
  getCaseContentFx,
  getCaseListFx,
  getUniqueContentFx,
  PurchaseOption,
  setSelectedPurchaseCurrency,
} from '@/entities/cases'
import { $casesList, $lastViewedCaseId, $userKeys } from '@/entities/cases'
import { useEffect, useState } from 'react'
import { CaseOpeningConfirmation } from '@/entities/cases/ui/caseOpeningConfirmation'
import { Spiner } from '@/shared/ui/spiner'
import Break from '@/shared/ui/break'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { CaseKind } from '@/entities/cases'
import { UniqueCaseReward } from '@/features/uniqueCaseReward'
import { RewardList } from '@/features/rewardList'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { $isBuyKeysModalVisible } from '@/entities/navigation/store'
import { useOpenCase } from '@/shared/hooks/useOpenCase'
import { PurchaseOptionButton } from '@/entities/cases/ui/purchaseOption'

export const ShopPageView = () => {
  useHideNav()
  const caseId = useUnit($lastViewedCaseId) ?? 1
  const userToken = useUnit($userToken)
  const userLang = useUnit($userLanguage)
  const userKeys = useUnit($userKeys)
  const userBalance = useUnit($userBalance)
  const caseList = useUnit($casesList)
  const isModalVisible = useUnit($isBuyKeysModalVisible)
  const caseKind = caseList?.find((_case) => _case.id == caseId)?.kind
  const { classes } = useStyles()
  const { handleOpenCase } = useOpenCase()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const selectedPurchaseOption = useUnit($selectedPurchaseOption)
  const texts = lang[userLang].pages.boxPage
  const currentCase = caseList?.find((box) => box.id === caseId)
  const caseName = currentCase?.name
  const folder = caseName?.replace(/\s/g, '').toLowerCase()
  const isUniqueRewards = caseKind === CaseKind.UNIQUE
  const rewardPreviewTitle = isUniqueRewards ? texts.standardRewards : texts.widgetTexts.lootBoxScaled.withinCase

  const byKindSorting = ['Steam On! Case', 'PS 5 Limited Case']
  const sortStrategy = byKindSorting.includes(caseName!) ? 'byKind' : 'byAmount'

  useBackButton({ forceHide: isModalVisible })

  useEffect(() => {
    if (userToken == null) {
      authUserFx()
      return
    }
    getCaseListFx({ token: userToken! })
    getCaseContentFx({ token: userToken!, id: caseId })
    if (isUniqueRewards) {
      getUniqueContentFx({ token: userToken!, id: caseId })
    }
  }, [userToken, caseId, isUniqueRewards])

  const handleCaseButtonClick = (option: PurchaseOption) => {
    if (!userToken) {
      goToAuth()
      return
    }

    if (!option.available) {
      goToShop({ tab: 2 })
      return
    }

    setSelectedPurchaseCurrency({ price: option.price, currency: option.currency })
    setIsModalOpen(true)
  }

  const removeModalWindow = () => {
    setIsModalOpen(false)
  }

  if (!caseList || !caseName) return <Spiner variant="secondary" />

  return (
    <div className={classes.root} data-ismodalopen={isModalOpen}>
      <CaseScaledCard lootBoxId={caseId} />
      {currentCase &&
        currentCase.purchase_options.map((option) => (
          <PurchaseOptionButton
            key={option.currency}
            {...option}
            amount={option.currency === 'keys' ? userKeys! : userBalance}
            onClick={() => handleCaseButtonClick(option)}
          />
        ))}
      {isModalOpen && (
        <>
          <div className={classes.backdrop}></div>
          <div className={classes.keyModalWindowWrapper} id="key-modal-window">
            <CaseOpeningConfirmation
              onConfirm={handleOpenCase}
              onDeny={removeModalWindow}
              caseName={caseName ?? ''}
              price={selectedPurchaseOption.price}
              currency={selectedPurchaseOption.currency}
            />
          </div>
        </>
      )}

      <div className={classes.expensiveWithHeadingnWrapper}>
        <UniqueCaseReward caseName={caseName!} />
        <span className={classes.expensiveText}>{rewardPreviewTitle}</span>
        <RewardList folder={folder!} sortStrategy={sortStrategy} mockForGiftCase={false} />
      </div>

      <Break />
      <div className={classes.description}>
        <h1>{texts.heading.description}</h1>
        {texts.descriptions[caseName]}
      </div>
    </div>
  )
}
